import { Stack } from 'component-library';
import { useEffect } from 'react';

import { BenchmarkType, PlotReportFactLabelNameEnum, R1FactType } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { NDVIHistoricalChart } from '@/components/Charts/NDVIHistorical';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useBenchmarksForOrganization } from '@/pages/shared/hooks/useBenchmarkForFact';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { isNullish } from '@/utils/booleans';

import { NoData } from '../../../../components/NoData';

export const NDVIGraphTile = ({
  zoom,
  setZoom,
  plotGroupId,
  graphType,
  selectedBenchmark,
  ...props
}: Partial<GraphTileProps & { selectedBenchmark?: string | null }>) => {
  const { setIndicatorGraphData } = useSetIndicatorHasData();
  const organizationId = useOrganizationId();

  const indicator = GraphTileType.ndvi;
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);
  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);

  const { [R1FactType.vegetation_condition_ndvi_historic_graph]: ndviHistoric } = useOrganizationIndicatorGraphFact<
    GraphFact[]
  >(R1FactType.vegetation_condition_ndvi_historic_graph, null, undefined, plotGroupId);

  const ndviBenchmarkFact = R1FactType.vegetation_condition_ndvi_benchmark_graph;
  const { [ndviBenchmarkFact]: highBenchmark } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    ndviBenchmarkFact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
    },
    plotGroupId,
  );
  const { [ndviBenchmarkFact]: moderateBenchmark } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    ndviBenchmarkFact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
    },
    plotGroupId,
  );
  const { [ndviBenchmarkFact]: lowBenchmark } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    ndviBenchmarkFact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
    },
    plotGroupId,
  );

  const lowBenchmarkData = (lowBenchmark?.value ?? [])?.map((fact) => {
    return { ...fact, name: `${fact.name}_low` };
  });
  const moderateBenchmarkData = (moderateBenchmark?.value ?? [])?.map((fact) => {
    return { ...fact, name: `${fact.name}_moderate` };
  });
  const highBenchmarkData = (highBenchmark?.value ?? [])?.map((fact) => {
    return { ...fact, name: `${fact.name}_high` };
  });

  const ndviBenchmarks = useBenchmarksForOrganization(
    R1FactType.vegetation_condition_ndvi_benchmark_graph,
    null,
    undefined,
    plotGroupId,
  );

  const noChartData = isNullish(ndviHistoric?.value);

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  if (noChartData) {
    return <NoData />;
  }

  const chartData = activeCustomBenchmark
    ? [...(ndviHistoric?.value ?? [])]
    : [...(ndviHistoric?.value ?? []), ...lowBenchmarkData, ...moderateBenchmarkData, ...highBenchmarkData];

  return (
    <Stack center data-testid='ndvi-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <ItemizedGraphTypeRenderer
          graphType={graphType}
          indicator={GraphTileType.ndvi}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          zoom={zoom}
          setZoom={setZoom}
          customBenchmark={activeCustomBenchmark}
          defaultGraphType={
            <NDVIHistoricalChart
              data={chartData}
              benchmarks={ndviBenchmarks}
              customBenchmark={activeCustomBenchmark}
              zoom={zoom}
              setZoom={setZoom}
            />
          }
        />
      </Stack>
    </Stack>
  );
};
